import { ApolloClient, HttpLink, InMemoryCache, ApolloLink } from "@apollo/client";
import { from } from "@apollo/client/core";
import { createApolloProvider } from "@vue/apollo-option";
import { onError } from "@apollo/client/link/error";
import { setContext } from "@apollo/client/link/context";
import { store } from "./store/store";
import { router } from "./router";
import getEnv from "./env";
import { ApolloProvider } from "@vue/apollo-option";

import NProgress from "nprogress";

NProgress.configure({ showSpinner: false });
const env = getEnv();

const httpEndpoint = env.VUE_APP_GRAPHQL_HTTP;

const errorHandler = onError(({ operation, networkError, graphQLErrors }) => {
    bobbeeLink.remove();
    console.error("onError started operation.operationName= ", operation.operationName);
    if (operation.query) {
        console.error(
            operation.query ? JSON.stringify(operation.query.definitions[0]) : "not query",
            graphQLErrors && graphQLErrors.length ? graphQLErrors[0] : "",
            networkError,
        );
    } else {
        console.error(
            operation.query?.definitions
                ? JSON.stringify(operation.query.definitions[0])
                : "not mutation",
            graphQLErrors && graphQLErrors.length ? graphQLErrors[0] : "",
            networkError,
        );
    }

    const isLoggedInUser =
        operation.query?.definitions.length &&
        "selectionSet" in operation.query.definitions[0] &&
        "selections" in operation.query.definitions[0].selectionSet &&
        "name" in operation.query.definitions[0].selectionSet.selections[0] &&
        operation.query.definitions[0].selectionSet.selections[0].name?.value === "loggedInUser";

    const isRecoverPassword =
        operation.query?.definitions.length &&
        "selectionSet" in operation.query.definitions[0] &&
        "selections" in operation.query.definitions[0].selectionSet &&
        "name" in operation.query.definitions[0].selectionSet.selections[0] &&
        operation.query.definitions[0].selectionSet.selections[0].name?.value ===
            "sendRecoverPasswordLink";

    const isRecoveryUser =
        operation.query?.definitions.length &&
        "name" in operation.query.definitions[0] &&
        operation.query.definitions[0].name?.value === "associatedUser";

    const isAccessForbidden =
        graphQLErrors?.length &&
        graphQLErrors.some((e: GraphQLError) => {
            const originalError = e.extensions?.originalError as { message?: string } | undefined;
            return originalError?.message === "Access forbidden"; // Fix typo: "forbiden" -> "forbidden"
        });

    if (
        !isRecoveryUser &&
        !isRecoverPassword &&
        !isLoggedInUser &&
        graphQLErrors?.some((e: GraphQLError) => e.extensions?.code === "AUTH_ERROR")
    ) {
        console.error("AuthError - location reload");
        window.location.reload();
    }

    if (isAccessForbidden) {
        console.error("Access forbiden - location reload");
        if (!(router.currentRoute.value.name === "dashboard")) {
            router.push({ name: "403" });
        }
    }

    const errorApollo = {
        request: operation.operationName,
        messages: graphQLErrors?.length
            ? graphQLErrors.map((x: GraphQLError) => {
                  const originalError = x.extensions?.originalError as
                      | { message?: string }
                      | undefined;
                  return originalError?.message || x.message || "ERROR";
              })
            : ["XXX"], // Ensures messages is always an array
        type: "error",
        errorType: "Apollo",
    };

    const errorMessage = Array.isArray(errorApollo.messages)
        ? errorApollo.messages.join(" | ")
        : String(errorApollo.messages);

    console.log(
        "%cError",
        "background: red; color: white; padding: 2px 4px; border-radius: 3px; font-weight: bold;",
        errorMessage || "Unknown Error", // Fallback message
    );

    store.commit("setApolloErrors", errorApollo);
});

// import { NotificationManager } from './services/notifications/notificationManager';
import { AuthServices } from "./services/auth.services";
import { GraphQLError } from "graphql/error";

// Config
const defaultOptions = {
    // You can use `https` for secure connection (recommended in production)
    httpEndpoint,
    // You can use `wss` for secure connection (recommended in production)
    // Use `null` to disable subscriptions
    // wsEndpoint: env.VUE_APP_GRAPHQL_WS,
    // sseEndPoint: env.VUE_APP_GRAPHQL_WS,
    sseEndPoint: null,
    // wsEndpoint: null,
    // LocalStorage token
    // Enable Automatic Query persisting with Apollo Engine
    persisting: true,
    // Use websockets for everything (no HTTP)
    // You need to pass a `wsEndpoint` for this to work
    websocketsOnly: false,
    // Is being rendered on the server?
    ssr: false,

    // Override default apollo link
    // note: don't override httpLink here, specify httpLink options in the
    // httpLinkOptions property of defaultOptions.
    link: errorHandler,
    // link: splitLink,
    defaultHttpLink: true,

    // Override default cache
    cache: new InMemoryCache(),
};

class BobbeeLink extends ApolloLink {
    counter: number = 0;
    add() {
        if (!this.counter) {
            NProgress.start();
        }
        this.counter++;
    }

    remove() {
        if (this.counter) {
            this.counter = 0;
            NProgress.done();
        }
    }

    request(operation, forward) {
        this.add();
        return forward(operation).map((data) => {
            // Called after server responds
            //   const time = new Date() - operation.getContext().start;
            //   console.log(`Operation ${operation.operationName} took ${time} to complete`);
            this.remove();
            return data;
        });
    }
}

const bobbeeLink = new BobbeeLink();

export abstract class ApolloServices {
    private static apolloProvider: ApolloProvider;
    private static apolloClient: ApolloClient<unknown>;
    static getCreateApolloProvider(options = {}) {
        if (this.apolloProvider) {
            return this.apolloProvider;
        }
        // Create apollo client
        const result = createApolloClient({
            ...defaultOptions,
            ...options,
        });
        this.apolloClient = result.apolloClient;

        // wsClient.connectionParams = () => ({
        //     headers: {
        //         Authorization: localStorage.getItem(AUTH_TOKEN) ? `Bearer ${localStorage.getItem(AUTH_TOKEN)}` : ''
        //     }
        // })
        // apolloClient.wsClient = wsClient

        // Create vue apollo provider
        this.apolloProvider = createApolloProvider({
            defaultClient: this.apolloClient,
            defaultOptions: {
                $query: {
                    fetchPolicy: "network-only",
                },
            },
        });
        return createApolloProvider({
            defaultClient: this.apolloClient,
        });
    }

    // Manually call this when user log out
    static async clearStore() {
        try {
            if (this.apolloClient) {
                await this.apolloClient.clearStore();
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.log("%cError on cache reset (logout)", "color: orange;", e.message);
        }
    }
}

// Create the apollo client
function createApolloClient({
    // Client ID if using multiple Clients
    clientId = "defaultClient",
    // URL to the HTTP API
    httpEndpoint,
    // Url to the Websocket API
    wsEndpoint = null,

    // Token used in localstorage
    // tokenName = 'apollo-token',

    // Is currently Server-Side Rendering or not
    ssr = false,
    // Only use Websocket for all requests (including queries and mutations)
    websocketsOnly = false,
    // Custom starting link.
    // If you want to replace the default HttpLink, set `defaultHttpLink` to false
    link = null,
    // Custom pre-auth links

    // If true, add the default HttpLink.
    // Disable it if you want to replace it with a terminating link using `link` option.
    defaultHttpLink = true,
    // Options for the default HttpLink
    httpLinkOptions = {},
    // Custom Apollo cache implementation (default is apollo-cache-inmemory)
    cache = null,
    // Options for the default cache
    inMemoryCacheOptions = {},
    // Additional Apollo client options
    apollo = {},
    // apollo-link-state options
    // clientState = null,
    // Function returning Authorization header token
    // getAuth = defaultGetAuth,
    // Local Schema
    typeDefs = undefined,
    // Local Resolvers
    resolvers = undefined,
    // Hook called when you should write local state in the cache
    onCacheInit = undefined,
}) {
    let authLink: ApolloLink, stateLink;
    const disableHttp = websocketsOnly && !ssr && wsEndpoint;

    if (!cache) {
        cache = new InMemoryCache(inMemoryCacheOptions);
    }

    if (!disableHttp) {
        const httpLink = new HttpLink({
            uri: httpEndpoint,
            ...httpLinkOptions,
        });
        if (!link) {
            link = httpLink;
        } else if (defaultHttpLink) {
            link = from([link, httpLink]);
        }

        // HTTP Auth header injection
        authLink = setContext(async (_, { headers }) => {
            const xData = {
                user: store.state.user.headerUser,
                company:
                    !store.state.inAdmin && store.state.selected_company
                        ? {
                              // name: store.state.selected_company.name,
                              accountingType:
                                  store.state.selected_company.profile?.accountingType ||
                                  "RECEIVABLES_DEBT",
                              taxSystem: store.state.selected_company.taxSystem || "BIC_Normal",
                              taxSystemType: store.state.selected_company.taxSystemType || "IS",
                          }
                        : undefined,
                accountingPeriodId:
                    store.state.accountingPeriod?.isMandatory &&
                    store.state.accountingPeriod?.selectedAccountingPeriod?.id
                        ? store.state.accountingPeriod.selectedAccountingPeriod.id
                        : "",
            };
            const Authorization = await AuthServices.getUserToken();
            const authorizationHeader = Authorization ? { Authorization } : {};
            const additionalHeader = {
                "X-userId": store.state.user?.id || "",
                "X-subUserId": store.state.subUserId || "",
                "X-companyId": !store.state.inAdmin ? store.state.selected_company?.id || "" : "",
                "X-accountingFirmId": store.state.inAdmin
                    ? store.state.user?.company?.id || ""
                    : store.state.companyAccountingFirm
                      ? store.state.companyAccountingFirm.id
                      : "",
                // "X-accountingPeriodId": store.state.accountingPeriod?.isMandatory && store.state.accountingPeriod?.selectedAccountingPeriod?.id ? store.state.accountingPeriod.selectedAccountingPeriod.id : "",
                "X-data": JSON.stringify(xData),
            };
            return {
                headers: {
                    ...headers,
                    ...authorizationHeader,
                    ...additionalHeader,
                },
            };
        });

        // Concat all the http link parts
        link = bobbeeLink.concat(authLink.concat(link));
    }

    // On the server, we don't want WebSockets and Upload links
    if (!ssr) {
        // If on the client, recover the injected state
        if (typeof window !== "undefined") {
            // eslint-disable-next-line no-underscore-dangle
            const state = window["__APOLLO_STATE__"];
            if (state && state[clientId]) {
                // Restore state
                cache.restore(state[clientId]);
            }
        }
    }

    const apolloClient = new ApolloClient({
        link,
        cache,
        // Additional options
        ...(ssr
            ? {
                  // Set this on the server to optimize queries when SSR
                  ssrMode: true,
              }
            : {
                  // This will temporary disable query force-fetching
                  ssrForceFetchDelay: 100,
                  connectToDevTools: false,
              }),
        typeDefs,
        resolvers,
        ...apollo,
    });

    // Re-write the client state defaults on cache reset
    if (stateLink) {
        apolloClient.onResetStore(stateLink.writeDefaults);
    }

    if (onCacheInit) {
        onCacheInit(cache);
        apolloClient.onResetStore(() => onCacheInit(cache));
    }

    return {
        apolloClient,
        stateLink,
    };
}
